/** @jsxImportSource theme-ui */
import { Box, Flex, Label, Select, Spinner, Text } from 'theme-ui';
import { Table } from '@component-controls/components';
import { useMemo, useState } from 'react';
import {
    useGetCompaniesQuery,
    useGetTicketsQuery,
    useGetTicketStatusesQuery,
} from '@/store/apiService';
import DropdownComboBox from '@/components/DropdownComboBox';
import { ticketColumns } from './columns';
import { ACTIVE_STATUS_IDS, toSelectOptions } from './common';

const Tickets: React.FC = () => {
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [companyIdFilter, setCompanyIdFilter] = useState<string>('');
    const [companySearch, setCompanySearch] = useState<string>('');
    const [activeOnly, setActiveOnly] = useState<boolean>(true);

    const statuses = useGetTicketStatusesQuery();
    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });
    const ticketsQuery = useGetTicketsQuery({
        Take: 200,
        OrderByDesc: 'Created',
        ...(companyIdFilter ? { CompanyId: companyIdFilter } : {}),
    });

    const statusOptions = toSelectOptions(statuses.data?.Results);
    const companyItems = (companies.data?.Results ?? []).map((c) => ({
        value: c.Id as string,
        label: c.Name ?? (c.Id as string),
    }));

    const rows = useMemo(() => {
        const all = ticketsQuery.data?.Results ?? [];
        return all.filter((t) => {
            if (activeOnly && t.CaseStatusId != null && !ACTIVE_STATUS_IDS.has(t.CaseStatusId)) {
                return false;
            }
            if (statusFilter && String(t.CaseStatusId) !== statusFilter) {
                return false;
            }
            return true;
        });
    }, [ticketsQuery.data, activeOnly, statusFilter]);

    return (
        <Flex sx={{ flexDirection: 'column', gap: 2 }}>
            <Flex sx={{ gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Box>
                    <Label htmlFor="ticket-status-filter">Status</Label>
                    <Select
                        id="ticket-status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All</option>
                        {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ minWidth: 280 }}>
                    <DropdownComboBox
                        label="Company"
                        placeholder="Search companies"
                        value={companyIdFilter}
                        items={companyItems}
                        isLoading={companies.isFetching}
                        total={companies.data?.Total}
                        onSearchChange={setCompanySearch}
                        onSelect={(item) => setCompanyIdFilter(item?.value as string ?? '')}
                    />
                </Box>
                <Label sx={{ width: 'auto', alignItems: 'center', gap: 1 }}>
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                    Active only
                </Label>
            </Flex>

            {ticketsQuery.isLoading ? <Spinner size={16} /> : null}

            {!ticketsQuery.isLoading && rows.length === 0 ? (
                <Text>No tickets found</Text>
            ) : (
                <Box
                    sx={{
                        borderRadius: 'sm',
                        overflowY: 'auto',
                        backgroundColor: 'background',
                        padding: 2,
                    }}
                >
                    <Table header sorting columns={ticketColumns as any} data={rows} />
                </Box>
            )}
        </Flex>
    );
};

export default Tickets;
